/* eslint-disable react-refresh/only-export-components */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";
import { useLocalstorageState } from "rooks";
import useSessionStorageState from "use-session-storage-state";

import {
  getBotTemplates as getBotTemplatesAPI,
  getOneBotTemplate,
} from "@/api";
import { useAuth } from "@/contexts/Auth/context";
import { BotTemplate, Name } from "@/types";

export interface DataState {
  botTemplates: BotTemplate[];
  names: Name[];
  initData(): void;
  getBotTemplates(): Promise<any>;
  getBotTemplateById(id: string): Promise<any>;
  reset(): void;
}

const initialState: DataState = {
  botTemplates: [],
  names: [],
  initData: () => {},
  getBotTemplates: () => new Promise(() => {}),
  getBotTemplateById: () => new Promise(() => {}),
  reset: () => {},
};

export const DataContext = createContext<DataState>(initialState);

export const useData = () => useContext(DataContext);

const STORAGE_KEY = "sec3-data-state";

interface Props {
  children: React.ReactNode;
}

export const DataProvider = ({ children }: Props) => {
  const [value] = useLocalstorageState(STORAGE_KEY, {
    botTemplates: [],
    names: [],
  });
  const [botTemplates, setBotTemplates] = useSessionStorageState<BotTemplate[]>(
    "bot-templates",
    {
      defaultValue: [],
    },
  );

  const initData = async () => {
    await initBotTemplates();
  };

  const initBotTemplates = async () => {
    const res = await getBotTemplatesAPI();
    setBotTemplates(res);
    return res;
  };

  const getBotTemplates = async () => {
    if (botTemplates.length === 0) {
      return await initBotTemplates();
    }
    return botTemplates;
  };

  const getBotTemplateById = async (id: string) => {
    let res: BotTemplate[];
    if (botTemplates.length === 0) {
      res = await initBotTemplates();
    } else {
      res = botTemplates;
    }
    return res.find((v) => v.id === id) ?? (await getOneBotTemplate(id));
  };

  // Clear cached bot templates and persisted state when the user signs
  // out; bot templates visible to each user may differ by plan and we
  // should never leak a previous user's cache into the next session.
  const reset = useCallback(() => {
    setBotTemplates([]);
    try {
      window.localStorage.removeItem(STORAGE_KEY);
      window.sessionStorage.removeItem("bot-templates");
    } catch {
      // ignore unavailable storage
    }
  }, [setBotTemplates]);

  const { isLoggedIn } = useAuth();
  const wasLoggedInRef = useRef(isLoggedIn);
  useEffect(() => {
    if (wasLoggedInRef.current && !isLoggedIn) {
      reset();
    }
    wasLoggedInRef.current = isLoggedIn;
  }, [isLoggedIn, reset]);

  return (
    <DataContext.Provider
      value={{
        getBotTemplates,
        initData,
        getBotTemplateById,
        reset,
        ...value,
        botTemplates,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};
